import { FC, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Box, Asset } from '@altermaind/web/ui';
import { SwitchDarkOrLightMode } from '@/_shared/components/SwitchDarkOrLightMode';
import { switchMode } from '@/_shared/styles/state/actions';
import { getModeTheme } from '@/_shared/styles/state/selectors';

type SidebarType = {
  icon?: string;
  imageBase64?: string;
  title?: string;
  hasToggle?: boolean;
  logoMaxHeight?: string;
  logoMaxWidth?: string;
};

/**
 * Sidebar of the home screen
 * @param icon - Optional, adds icon to title box
 * @param imageBase64 - Optional, adds base64 image to title box
 * @param title - the title of the sidebar
 * @param children - the body of the sidebar
 * */
export const Sidebar: FC<PropsWithChildren<SidebarType>> = ({
  icon,
  imageBase64,
  title,
  children,
  logoMaxHeight = '40px',
  logoMaxWidth = '200px',
  hasToggle = false,
}) => {
  const dispatch = useDispatch();
  const currentMode = useSelector(getModeTheme());
  const localMode = localStorage.getItem('mode');
  const [check, setCheck] = useState(currentMode !== 'light');

  const toggleMode = useCallback(() => {
    if (check) {
      dispatch(switchMode('light'));
      localStorage.setItem('mode', 'light');
      setCheck(false);
    } else {
      dispatch(switchMode('dark'));
      localStorage.setItem('mode', 'dark');
      setCheck(true);
    }
  }, [check, dispatch]);

  useEffect(() => {
    if (localMode === 'light' && localMode !== currentMode) {
      dispatch(switchMode('light'));
      setCheck(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      display='flex'
      flexDirection='column'
      flex={1}
      height='100%'
      maxHeight='100vh'
      alignContent='center'
      style={{ boxSizing: 'border-box' }}
      bg='secondary/background'
      padding='s'
      m='s'
      borderRadius='m'
      pt={icon ? undefined : 'l'}
      position='sticky'
      left='0'
      top='0'
      overflowY='auto'
    >
      {(icon || title || imageBase64) && (
        <Box mb='xl' display='flex' alignItems='center'>
          {icon ? (
            <Asset
              width={title === '' ? '100%' : 'auto'}
              maxHeight={title === '' ? '' : logoMaxHeight}
              maxWidth={logoMaxWidth}
              name={icon}
              domain='images'
              mr='xs'
              data-testid='logo'
            />
          ) : (
            imageBase64 && (
              <Asset
                width={title === '' ? '100%' : 'auto'}
                maxHeight={title === '' ? '' : logoMaxHeight}
                maxWidth='fit-content'
                src={imageBase64}
                mr='xs'
                data-testid='logo'
              />
            )
          )}
          {title && <Typography variant='h4'>{title}</Typography>}
        </Box>
      )}
      <Box height='100%'>{children}</Box>
      {hasToggle && (
        <Box width='100%' display='flex' justifyContent='flex-end' height={34}>
          <SwitchDarkOrLightMode isDarkMode={check} onClick={toggleMode} />
        </Box>
      )}
    </Box>
  );
};
